/* A dataset's trend, reduced to what a ledger row and a regression detector need. Pure.
 *
 * `deltaDisplay` already answers *how far and which way*, with its four honest non-answers. What
 * this adds is the pair a coverage detector needs — the coverage at each end of the window — and
 * the raw values a sparkline draws.
 *
 * **Coverage is read at the same two points the delta is measured between**, not at the ends of the
 * array. A window whose first two days hold no measurement would otherwise report a coverage change
 * against a day the score comparison ignored, and the two figures would describe different periods
 * while sitting on one row.
 */

import type { Trend } from '../api/scoring';

import { deltaDisplay } from './presentation';
import type { Movement } from './types';

export function movementOf(trend: Trend): Movement {
  const measured = trend.points.filter((point) => point.value !== null);
  const first = measured[0];
  const last = measured[measured.length - 1];

  return {
    delta: deltaDisplay(trend),
    points: trend.points.map((point) => point.value),
    /* `?? null` on a field that is already `number | null` collapses only `undefined`, which is
       what an empty window yields. It cannot turn a measured zero into an absence. */
    coverageNow: last?.column_coverage ?? null,
    coverageThen: first?.column_coverage ?? null,
    crossesModelChange: trend.crosses_a_model_change,
  };
}

/** The window every movement on this screen is measured over, in days.
 *
 * A constant rather than a caller's argument wherever it is *labelled*: the column header names the
 * window and the two must not be able to disagree. The header control may widen it, and when it
 * does the label moves with it — that is one value flowing to both, not two values agreeing.
 */
export const MOVEMENT_WINDOW_DAYS = 7;

/** The windows the period control offers. Bounded by the contract: `days` on the trend route is
 * 1–730, and the estate trend is 2–366. These three are what a reader actually compares over. */
export const PERIOD_OPTIONS: readonly number[] = [7, 30, 90];
